import React, { createContext, PropsWithChildren, useState } from 'react';
import { TableError, TableParams, TableParamsType } from '../../../types/common';
import { AxiosInstance } from 'axios';

const DEFAULT_TABLE_PARAMS: TableParams = {
  filter: '',
  sorting: '',
  skipCount: 0,
  maxResultCount: 10,
};

export type DynamicTableContextValue = {
  tableInfo: TableParams;
  setTableInfo: (value: TableParams) => void;
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  checkedValues: string[];
  setCheckedValues: (value: string[]) => void;
  errors: TableError[];
  setErrors: (value: TableError[]) => void;
  handleShow?: (id: string, event: any, popoverInfo?: string) => void;
  registerPermissionHandler?: any;
  labeledFilter: any[];
  setLabeledFilter: any;
  axiosInstance: AxiosInstance | null;
  isSelectAllActive: boolean;
  setIsSelectAllActive: (value: boolean) => void;
  isPageSelectAllActive: boolean;
  setIsPageSelectAllActive: (value: boolean) => void;
  uncheckedIds: string[];
  setUncheckedIds: (value: string[]) => void;
};

const DynamicTableContext = createContext<DynamicTableContextValue | undefined>(undefined);

const DynamicTableProvider = ({
  children,
  ...rest
}: PropsWithChildren<{ tableInfo: any; axiosInstance?: AxiosInstance | null }>) => {
  const [tableInfo, setTableInfo] = useState<TableParams>({
    ...DEFAULT_TABLE_PARAMS,
    ...(rest.tableInfo ?? {}),
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [handleShow, registerPermissionHandler] =
    useState<(id: string, event: any, popoverInfo?: string) => void>();
  const [errors, setErrors] = useState<TableError[]>([]);
  const [checkedValues, setCheckedValues] = useState<string[]>([]);
  const [labeledFilter, setLabeledFilter] = useState<Record<string, string>[]>([]);
  const [isSelectAllActive, setIsSelectAllActive] = useState(false);
  const [isPageSelectAllActive, setIsPageSelectAllActive] = useState(false);
  const [uncheckedIds, setUncheckedIds] = useState<string[]>([]);

  const contextValue: DynamicTableContextValue = {
    tableInfo,
    setTableInfo,
    searchQuery,
    setSearchQuery,
    checkedValues,
    setCheckedValues,
    errors,
    setErrors,
    registerPermissionHandler,
    handleShow,
    labeledFilter,
    setLabeledFilter,
    axiosInstance: rest.axiosInstance ?? null,
    isSelectAllActive,
    setIsSelectAllActive,
    isPageSelectAllActive,
    setIsPageSelectAllActive,
    uncheckedIds,
    setUncheckedIds,
  };

  return (
    <DynamicTableContext.Provider value={contextValue}>{children}</DynamicTableContext.Provider>
  );
};

export { DynamicTableContext, DynamicTableProvider };
